// Text anchoring for inline translation comments, following the W3C Web
// Annotation model: a fragment is stored as character offsets into the
// container's plain text plus an exact `quote` and surrounding `prefix`/
// `suffix`. Offsets give an O(1) re-locate; the quote/context let us recover
// the fragment when a re-render shifts the offsets.

export const CONTEXT_CHARS = 40;

// Character index of a Range boundary within `container`'s text. Measuring via
// a sub-range's toString() keeps this consistent with container.textContent
// (both are text nodes concatenated in document order) and is agnostic to
// whether the boundary sits in a text node or an element.
function boundaryOffset(container, node, offset) {
  const r = document.createRange();
  r.selectNodeContents(container);
  r.setEnd(node, offset);
  return r.toString().length;
}

// Map a character offset back to a { node, offset } text-node position.
function findTextPosition(container, target) {
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  let count = 0;
  let node;
  while ((node = walker.nextNode())) {
    const len = node.textContent.length;
    if (count + len >= target) {
      return { node, offset: target - count };
    }
    count += len;
  }
  return null;
}

export function offsetsToRange(container, start, end) {
  const startPos = findTextPosition(container, start);
  const endPos = findTextPosition(container, end);
  if (!startPos || !endPos) return null;
  const range = document.createRange();
  range.setStart(startPos.node, startPos.offset);
  range.setEnd(endPos.node, endPos.offset);
  return range;
}

// Describe the current selection relative to `container`, or null if the
// selection is empty or falls outside the container.
export function describeSelection(container) {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return null;
  const range = sel.getRangeAt(0);
  if (!container.contains(range.commonAncestorContainer)) return null;

  const quote = range.toString();
  if (!quote.trim()) return null;

  const full = container.textContent;
  const start = boundaryOffset(container, range.startContainer, range.startOffset);
  const end = start + quote.length;
  return {
    quote,
    prefix: full.slice(Math.max(0, start - CONTEXT_CHARS), start),
    suffix: full.slice(end, end + CONTEXT_CHARS),
    start,
    end,
    rect: range.getBoundingClientRect(),
  };
}

// Re-locate a stored comment in the current DOM, returning a Range or null.
// Offsets first (exact-match guard), then prefix+quote+suffix, then the bare
// quote. Null means the translation changed too much to anchor confidently.
export function reanchor(container, c) {
  const full = container.textContent;
  if (full.slice(c.start_offset, c.end_offset) === c.quote) {
    return offsetsToRange(container, c.start_offset, c.end_offset);
  }
  const contextual = `${c.prefix}${c.quote}${c.suffix}`;
  if (contextual) {
    const idx = full.indexOf(contextual);
    if (idx !== -1) {
      const s = idx + c.prefix.length;
      return offsetsToRange(container, s, s + c.quote.length);
    }
  }
  const bare = full.indexOf(c.quote);
  if (bare !== -1) return offsetsToRange(container, bare, bare + c.quote.length);
  return null;
}
